import { Autocomplete, TextField, Button } from "@mui/material";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux";
import { debounceFunction, genKey } from "../../../utils/utilFunctions";
import { treeActions } from "../../../redux/slices/treeSlice";
import { cloneDeep, isArray, isEqual } from "lodash";
import { getFieldValidationRules, getDataTypeMaxLengt} from "../../../utils/validate";
import { eachDeep } from "deepdash-es/standalone";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LightTooltip from "../../../commonComponents/LightTooltip";
import { api } from "../../../redux/api";

export const FieldPropHL7Datatype = () => {
  //util
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //redux refs
  const parentScreen = useContext(ParentScreenCtx);
  const { account } = useSelector((state) => state.queryParams);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
  const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
  const templateHeader = rightTree.tree.templateHeader;

  //states
  const [value, setValue] = useState(selectedNode.DATA_TYPE || t('SELECT'));
  const [loaded, setLoaded] = useState(false);
  const [messageWarningIcon, setMessageWarningIcon] = useState(<HelpOutlineIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>);
  const [fieldData, setFieldData] = useState([]);

  //handle dropdown cvalue changing via selection
  const handleDropdownChangeOuter = async (e, val) => {
    setValue(val);
  }

  //set dropdown state on initial node selection
  const setInitialValue = () => {
    const dataType = selectedNode.DATA_TYPE || selectedNode.HL7_DATA_TYPE
    if (selectedNode && dataType) {
      if (dropdownOptions.includes(dataType)) setValue(dataType);
    } else {
      setValue(t('SELECT'));
    }
    setLoaded(true);
  }

  //set new node props and children if field converted to field group
  const writeNewNodeProps = (node, val, nodeParent) => {
    let newNode = cloneDeep(node);
    let dtMap = datatypes[val];
    if (dtMap === 'field') {
      //HL7 datatype converts node to field
      if ((node.FIELD_GROUP && node.FIELD_GROUP === "TRUE") || (node.type && node.type === "FIELD_GROUP") || node.ICON_DISPLAY === "FIELD_GROUP" || node.ICON_DISPLAY === "REPEATING_FIELD_GROUP") {
        //node is Field Group. Need to convert to Field and delete children.
        newNode = {
          FIELDTYPE: "ALPHA",
          ...(parentScreen === 'outbound' ? {FILLCHAR: " "} : {}),
          MAPNAME: node.MAPNAME ? node.MAPNAME : node.DESCRIPTION || node.FIELD_NAME || node.TYPE || "",
          // DESCRIPTION: node.DESCRIPTION || node.FIELD_NAME || node.MAPNAME || "",
          FIELD_NAME: node.FIELD_NAME ? node.FIELD_NAME : node.DESCRIPTION || node.MAPNAME || node.TYPE || "",
          ...(parentScreen === 'outbound' ? {PADDIR: "RIGHT"} : {}),
          PARENT: node.PARENT,
          RECORD_TYPE: node.RECORD_TYPE || node.PARENT || "NONE",
          ...(parentScreen === 'outbound' ? {SOURCE_TYPE: "INBOUND_DATA"} : {}),
          nodeId: node.nodeId || genKey(),
          key: node.key || {header: parentScreen=== "outbound" ? "obh-0" : "ibh-0", body: parentScreen=== "outbound" ? "ob-0" : "ib-0", trailer: parentScreen=== "outbound" ? "obt-0" : "ibt-0"}[rightTree.currentTab],
          title: `${t('TIT_FIELD')}: ${node.DESCRIPTION || node.FIELD_NAME || node.MAPNAME || node.TYPE || ""}`,
          children:  [],
          ICON_DISPLAY: "FIELD",
          MAX_LENGTH: fieldData[0]?.length || "",
          forceExpanded: true,
          fieldNumber: node.fieldNumber || 1,
            ...(fieldData[0]?.reqOpt && fieldData[0]?.reqOpt === "R" ? { REQUIRED: fieldData[0]?.reqOpt === "R" ? "TRUE" : "FALSE" } : {}),
        
        }
        
      }
      newNode.DATA_TYPE = val;
      if(!node.FIELD_GROUP || node.ICON_DISPLAY === "FIELD" || node.ICON_DISPLAY === "REPEATING_FIELD"){
        if(parentScreen === 'outbound' && newNode.RECORD_TYPE) {
          newNode.RECORD_TYPE = "NONE";
          newNode.SOURCE_TYPE = "INBOUND_DATA";
        }
        newNode.MAX_LENGTH = fieldData[0]?.length || "";
        if(fieldData[0]?.reqOpt && fieldData[0]?.reqOpt === "R")  newNode.REQUIRED = "TRUE" ;
        else delete newNode.REQUIRED;
        
        delete newNode.HL7_DATA_TYPE;
      } 
    } else if (typeof dtMap === "object") {
      //HL7 datatype converts node to fieldgroup
      // const fieldGroupData = fieldData.find(o => o.TYPE === "FIELD_GROUP");
      const loopNodeChildren = (childrenArr, parentNode) => {
        return childrenArr?.map((o, i) => {
          let nd = {};
          if ((o.TYPE && o.TYPE === "FIELD_GROUP") || (o.hasChildren && o.subComponents && o.subComponents.length > 0)) {
            //make Field Group node            
            nd = {
              FIELD_GROUP: "TRUE", 
              HL7_DATA_TYPE: o.DATA_TYPE || o?.component?.dataTypeCode || o?.dataTypeCode,
              COMPONENT_TYPE: "SUBCOMPONENT",
              TYPE: o.NAME || o?.component?.description || o?.description,
              // ELEMENT_ID: "",
              ICON_DISPLAY: "FIELD_GROUP",
              PARENT: (parentNode.DESCRIPTION || parentNode.MAPNAME || "NONE" ),
              DESCRIPTION: o.NAME || o?.component?.description || o?.description,
              ...(parentScreen === 'outbound' ? {LOOP: "MULTI", EMPTY_ACTION: "EMPTY"} : {}),
              EDIT_RULE: [{
                MAX_LENGTH: o.length,
                ...(o.reqOpt && o.reqOpt === "R" ? { REQUIRED: o.reqOpt === "R" ? "TRUE" : "FALSE" } : {}),
                TYPE: "RECORD_RULE"
              }],
              // REPEATING_FIELD: false,
              nodeId: genKey(o.NAME || o?.component?.description || o?.description),
              key: `${parentNode.key}-${i}`,
              title:`${o.NAME || o?.component?.description || o?.description || o.TYPE}`,
              isLeaf: false,
              ...(parentScreen === 'inbound' ? {TYPE: o.MAPNAME || o?.component?.description || o?.description} : {})
            };
            if (o.children || o?.subComponents) {
              nd.children = loopNodeChildren(o.children || o?.subComponents, nd);
            }
          } else {
            // Field node            
            nd = {
              DATA_TYPE: o.DATA_TYPE || o?.component?.dataTypeCode || o?.dataTypeCode,
              FIELDTYPE: "ALPHA",
              FIELD_NAME: o.NAME || o?.component?.description || o?.description,
              MAPNAME: o.NAME || o?.component?.description || o?.description,
              // DESCRIPTION: o.NAME,
              RECORD_TYPE: encodeURIComponent(parentNode.DESCRIPTION || parentNode.MAPNAME || "NONE" ),
              MAX_LENGTH: o.length,
              ...(o.reqOpt && o.reqOpt === "R" ? { REQUIRED: "TRUE" } : delete nd.REQUIRED),
              // REPEATING_FIELD: false,
              nodeId: genKey(o.NAME || o?.component?.description || o?.description),
              key: `${parentNode.key}-${i}`,
              title: `${t('TIT_FIELD')}: ${o.NAME || o?.component?.description || o?.description || o.TYPE}`,
              isLeaf: true,
              children: [],
              ICON_DISPLAY: "FIELD",
              fieldNumber: i+1,
              ...(parentScreen === 'outbound' ? {SOURCE_TYPE: "INBOUND_DATA"} : {}),

            }
          }
          return nd;
        });
      }

      if ((node.FIELD_GROUP && node.FIELD_GROUP === "TRUE") || (node.type && node.type === "FIELD_GROUP") || node.ICON_DISPLAY === "FIELD_GROUP" || node.ICON_DISPLAY === "REPEATING_FIELD_GROUP") {
        //node is already Field Group. Need to update props and assign new children nodes.
      
        newNode.HL7_DATA_TYPE = val;
        newNode.COMPONENT_TYPE = nodeParent.FIELD_GROUP ? "SUBCOMPONENT" : "COMPONENT";  
        
        // Update EditRule with new validation 
        newNode.EDIT_RULE = [{
          MAX_LENGTH: fieldData[0]?.length || "",
          ...(fieldData[0]?.reqOpt && fieldData[0]?.reqOpt === "R" ? { REQUIRED: "TRUE" } : delete newNode.REQUIRED),
          TYPE: "RECORD_RULE"
        }];
        
        // Replace children with new datatype structure 
        // newNode.children = loopNodeChildren(dtMap, newNode);
        newNode.children = fieldData && fieldData.length > 0 ? loopNodeChildren(fieldData[0]?.components || fieldData[0]?.subComponents, newNode) : loopNodeChildren(dtMap, newNode);
        if (newNode.children && newNode.children.length > 0) {
          newNode.isLeaf = false;
        }
      } else {
        //node is Field that needs to convert to a Field Group, with children added.
        if (selectedNode.FIELDTYPE === "ALPHA") {
                
        newNode = {
          COMPONENT_TYPE: nodeParent.FIELD_GROUP ? "SUBCOMPONENT" : "COMPONENT",
          ...(parentScreen === 'outbound' ? {DESCRIPTION: node.DESCRIPTION || node.MAPNAME || node.TYPE || ""} : {}),
          EDIT_RULE: [{
            MAX_LENGTH: fieldData[0]?.length || "",
            ...(fieldData[0]?.reqOpt && fieldData[0]?.reqOpt === "R" ? { REQUIRED: "TRUE" } : delete newNode.REQUIRED),
            TYPE: "RECORD_RULE"
          }],
          // ELEMENT_ID: "",
          ...(parentScreen === 'outbound' ? {EMPTY_ACTION: "EMPTY"} : {}),
          FIELD_GROUP: "TRUE",
          ICON_DISPLAY: "FIELD_GROUP",
          ...(parentScreen === 'outbound' ? {LOOP: "SINGLE"} : {}),
          PARENT: nodeParent.PARENT || nodeParent.MAPNAME || nodeParent.DESCRIPTION || "NONE" ,
          nodeId: node.nodeId || genKey(),
          key: node.key || {header: parentScreen === 'outbound' ? "obh-0" : "ibh-0", body: parentScreen === 'outbound' ? "ob-0" : "ib-0", trailer: parentScreen === 'outbound' ? "obt-0" : "ibt-0"}[rightTree.currentTab],
          forceExpanded: true,
          title:`${t('FIELD_GROUP')}: ${node.DESCRIPTION || node.MAPNAME || node.TYPE || ""}`,
          ...(parentScreen === 'inbound' ? {TYPE: node.MAPNAME} : {})
        }
           newNode.children = fieldData && fieldData.length > 0 ? loopNodeChildren(fieldData[0]?.components || fieldData[0]?.subComponents, newNode) : loopNodeChildren(dtMap, newNode);
           if (newNode.children && newNode.children.length > 0) {
             newNode.isLeaf = false;
           }
        }
      }
    }
    if (selectedNode.FIELDTYPE === "ALPHA") newNode.HL7_DATA_TYPE = val;
    return newNode;
  }

  //clone and dispatch new object for tData and selectedNode
  const setNodeData = (value, cleanNode) => {
    if (selectedNode.HL7_DATA_TYPE === value || selectedNode.DATA_TYPE === value) return; //if same, don't perform a change
    if (value === "var") return; //var, don't change data.
    // Prevent HL7 datatype changes for nested nodes (components/subcomponents inside field groups)
    const pNode = getParentNode(selectedNode, translatedData);
    const isFieldGroupNode = (node) => {
      if (!node) return false;
      return (
        node.FIELD_GROUP === "TRUE" ||
        node.type === "FIELD_GROUP" ||
        node.ICON_DISPLAY === "FIELD_GROUP" ||
        node.ICON_DISPLAY === "REPEATING_FIELD_GROUP"
      );
    };
    
    if (selectedNode.COMPONENT_TYPE === "SUBCOMPONENT" || isFieldGroupNode(pNode)) {
      return; // Don't allow changes for nested nodes
    }
    
    const parentKey = selectedNode.key.split("-").slice(0, -1).join("-");
    if (value !== "") {
      let nodeParent = false;
      let newSelectedNode = cloneDeep(selectedNode);
      let isConvertingToFieldGroup = false;
      let convertedNodeKey = null;
      let updatedData = eachDeep(cloneDeep(translatedData), (node, key, parentArr, ctx) => {
        if (node?.key && node.key === parentKey) {
          nodeParent = node;
        }
        if (typeof node !== 'object') return;
        //if this iteration is target node.
        if (node?.nodeId && node.nodeId === selectedNode.nodeId) {
          if (cleanNode) {
            //remove data_type fromdelete prop option
            if (selectedNode?.FIELD_GROUP === "TRUE" || selectedNode?.type === "FIELD_GROUP") {
              if (node.HL7_DATA_TYPE) delete node.HL7_DATA_TYPE;
              if (newSelectedNode.HL7_DATA_TYPE) delete newSelectedNode.HL7_DATA_TYPE;
            } else {
              if (node.DATA_TYPE) delete node.DATA_TYPE;
              if (newSelectedNode.DATA_TYPE) delete newSelectedNode.DATA_TYPE;
            }
          } else {
            //set find node structure and assign
            const originalIconDisplay = node.ICON_DISPLAY;
            node["children"] = [];
            let newNode = writeNewNodeProps(node, value, nodeParent);
            newSelectedNode = newNode;
            
            const wasFieldGroup = originalIconDisplay === "FIELD_GROUP" || originalIconDisplay === "REPEATING_FIELD_GROUP" ;
            const isNowFieldGroup = newNode.ICON_DISPLAY === "FIELD_GROUP" || newNode.ICON_DISPLAY === "REPEATING_FIELD_GROUP" || newNode.FIELD_GROUP === "TRUE";
            
            if ((originalIconDisplay === "FIELD" && isNowFieldGroup) ||  (wasFieldGroup && isNowFieldGroup && newNode.children && newNode.children.length > 0)) {
              isConvertingToFieldGroup = true;
              convertedNodeKey = newNode.key;
            }
            
            let nodeKeys = Object.keys(node);
            let newNodeKeys = Object.keys(newNode);

            //add / mod keys to node
            newNodeKeys.forEach(pString => {
              node[pString] = newNode[pString];
            });

            //delete keys in node
            nodeKeys.forEach(pString => {
              if (!newNodeKeys.includes(pString)) delete node[pString];
            });
            if (datatypes[value] === 'field' && (!node.FIELD_GROUP || node.ICON_DISPLAY === "FIELD" || node.ICON_DISPLAY === "REPEATING_FIELD")) {
              delete node.HL7_DATA_TYPE;
            }
          }
        }
      });

      //prevent dispatch for no reason
      if (isEqual(translatedData, updatedData)) return;

      //set new trans data to updatedData.
      dispatch(
        treeActions.setNewRightTreeTranslatedData({
          screen: parentScreen,
          type: rightTree.currentTab,
          data: updatedData,
        })
      );

      // If we converted a field to a field group, trigger tree expansion
      if (isConvertingToFieldGroup && convertedNodeKey) {
        dispatch(
          treeActions.setTreeActions({
            data: {
              expandKeys: [convertedNodeKey],
              treeType: rightTree.currentTab
            }
          })
        );
      }

      //set selectedNode to match.
      dispatch(
        treeActions.setRightTreeSelectedNode({
          screen: parentScreen,
          type: rightTree.currentTab,
          data: { ...newSelectedNode},
        })
      );
    }
  }

  useEffect(() => {
    if (loaded && selectedNode && value && value !== t('SELECT')) {
      const loadFieldData = async () => {
        try {
          const response = await dispatch(api.endpoints.getHL7DatatypeComponents.initiate({
            account, 
            data_type: value
          }, {
            forceRefetch: true
          }));
          
          if (response?.data && response?.data?.current_state === "SUCCESS") {
            setFieldData(response.data.data);
          } else {
            setFieldData([]);
          }
        } catch (error) {
          console.log('API call failed:', error);
          setFieldData([]);
        }
      };
      
      loadFieldData();
    } else if (value === t('SELECT') || !value) {
      setFieldData([]);
    }
  }, [value, loaded, selectedNode]);

  useEffect(() => {
    if (!loaded || !selectedNode) return;
    return debounceFunction(() => setNodeData(value, (value === t('SELECT'))));    
    
  }, [fieldData]);

  useEffect(() => {
    if (templateHeader.FILETYPE !== "HL7" && loaded) setNodeData(value, true);
  }, [templateHeader.FILETYPE]);

  useEffect(() => {
    setLoaded(false);
  }, [selectedNode.key])

  useEffect(() => {
    if (!loaded) return setInitialValue()
  }, [loaded]);

  // const handleDropdownChangeInner = (e) => {
  //  inner function maybe not needed because user must select an item from dropdown, not assign custom value.
  //   console.log(e?.target?.value);
  // }

  //TODO: ask Scott or Terry about this improvement.
  //set dropdown options. if node is a subcomponent, only show valid items.
  const pNode = getParentNode(selectedNode, translatedData);

  // Helper function to check if a node is a field group
  const isFieldGroup = (node) => {
    if (!node) return false;
    return (
      node.FIELD_GROUP === "TRUE" ||
      node.type === "FIELD_GROUP" ||
      node.ICON_DISPLAY === "FIELD_GROUP" ||
      node.ICON_DISPLAY === "REPEATING_FIELD_GROUP"
    );
  };

  // Check if this is a nested node that should not allow HL7 datatype changes
  const isNestedNode = () => {
    // If the node itself has COMPONENT_TYPE "SUBCOMPONENT", it's nested
    if (selectedNode.COMPONENT_TYPE === "SUBCOMPONENT") {
      return true;
    }
    
    // If the parent node is a field group, then this node is nested
    if (isFieldGroup(pNode)) {
      return true;
    }
    
    return false;
  };

  let ddList = selectedNode.HL7_DATA_TYPE ? 
    [selectedNode.HL7_DATA_TYPE, ...dropdownOptions]
    : dropdownOptions;
  let ddOptions = [t('SELECT'), ...ddList.filter((str, index) => ddList.indexOf(str) === index).sort()]
  

  return (<div className="flex width-100">
    <Autocomplete
      disabled={!loaded || isNestedNode()}
      id="hl7DatatypeDropdown"
      size="small"
      // freeSolo
      options={ddOptions}
      value={value}
      onChange={handleDropdownChangeOuter}
      renderInput={(params) => <TextField
        {...params}
        label={t('LBL_HL7_DATA_TYPE')}
        value={value}
      // onChange={handleDropdownChangeInner}
      />}
      sx={{flex: "1"}}
    />
    {
      !messageWarningIcon ? '' :
      <LightTooltip className="margin-left-4" title={t('BOX_HL7_DATA_TYPE_CHANGE_WARNING_OUTBOUND')} placement="right-start">
        <Button 
          sx={{
            width: '30px !important', 
            padding: '4px 8px !important', 
            minWidth: '30px !important'
          }}
          size="small"
          onClick={() => setMessageWarningIcon(false)}
          onMouseEnter={() => setMessageWarningIcon(<VisibilityOffIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>)}
          onMouseLeave={() => setMessageWarningIcon(<HelpOutlineIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>)}
        >
          {messageWarningIcon || ''}
        </Button>
      </LightTooltip>
    }
  </div>
    
  )
}

const getParentNode = (selectedNode, translatedData) => {
  let node;
  const parentKey = selectedNode.key.split("-").slice(0, -1).join("-")
  eachDeep(cloneDeep(translatedData), (obj) => {
    if (obj && obj.key && obj.key === parentKey) {
      node = obj
      return false;
    }
    if (isArray(obj?.children)) {
      return obj.children;
    }
  })
  return node;
}

const datatypes = {
  AD: "field",
  AUI: [
    { DATA_TYPE: "ST", NAME: "Authorization Number" },
    { DATA_TYPE: "DT", NAME: "Date" },
    { DATA_TYPE: "ST", NAME: "Source" }
  ],
  CCD: [
    { DATA_TYPE: "ID", NAME: "Invocation Event" },
    {
      DATA_TYPE: "TS", NAME: "Date_time", TYPE: "FIELD_GROUP", children: [
        { DATA_TYPE: "DTM", NAME: "Time" },
        { DATA_TYPE: "ID", NAME: "Degree of Precision" }
      ]
    }
  ],
  CCP: [
    { DATA_TYPE: "NM", NAME: "Channel Calibration Sensitivity Correction Factor" },
    { DATA_TYPE: "NM", NAME: "Channel Calibration Baseline" },
    { DATA_TYPE: "NM", NAME: "Channel Calibration Time Skew" }
  ],
  CD: [
    {
      DATA_TYPE: "WVI", NAME: "Channel Identifier", TYPE: "FIELD_GROUP", children: [
        { DATA_TYPE: "NM", NAME: "Channel Number" },
        { DATA_TYPE: "ST", NAME: "Channel Name" }
      ]
    },
    {
      DATA_TYPE: "WVS", NAME: "Waveform Source", TYPE: "FIELD_GROUP", children: [
        { DATA_TYPE: "ST", NAME: "Source One Name" },
        { DATA_TYPE: "ST", NAME: "Source Two Name" }
      ]
    },
    {
      DATA_TYPE: "CSU", NAME: "Channel Sensitivity_Units", TYPE: "FIELD_GROUP", children: [
        { DATA_TYPE: "NM", NAME: "Channel Sensitivity" },
        { DATA_TYPE: "ST", NAME: "Unit of Measure Identifier" },
        { DATA_TYPE: "ST", NAME: "Unit of Measure Description" },
        { DATA_TYPE: "ID", NAME: "Unit of Measure Coding System" },
        { DATA_TYPE: "ST", NAME: "Alternate Unit of Measure Identifier" },
        { DATA_TYPE: "ST", NAME: "Alternate Unit of Measure Description" },
        { DATA_TYPE: "ID", NAME: "Alternate Unit of Measure Coding System" },
      ]
    },
    {
      DATA_TYPE: "CCP", NAME: "Channel Calibration Parameters", TYPE: "FIELD_GROUP", children: [
        { DATA_TYPE: "NM", NAME: "Channel Calibration Sensitivity Correction Factor" },
        { DATA_TYPE: "NM", NAME: "Channel Calibration Baseline" },
        { DATA_TYPE: "NM", NAME: "Channel Calibration Time Skew" },
      ]
    },
    { DATA_TYPE: "NM", NAME: "Channel Sampling Frequency" },
    {
      DATA_TYPE: "NR", NAME: "Minimum_Maximum Data Values", TYPE: "FIELD_GROUP", children: [
        { DATA_TYPE: "NM", NAME: "Low Value" },
        { DATA_TYPE: "NM", NAME: "High Value" },
      ]
    },
  ],
  CE: [
    { DATA_TYPE: "ST", NAME: "Identifier" },
    { DATA_TYPE: "ST", NAME: "Text" },
    { DATA_TYPE: "ID", NAME: "Name of Coding System" },
    { DATA_TYPE: "ST", NAME: "Alternate Identifier" },
    { DATA_TYPE: "ST", NAME: "Alternate Text" },
    { DATA_TYPE: "ID", NAME: "Name of Alternate Coding System" },
  ],
  CF: [
    { DATA_TYPE: "ST", NAME: "Identifier" },
    { DATA_TYPE: "FT", NAME: "Formatted Text" },
    { DATA_TYPE: "ID", NAME: "Name of Coding System" },
    { DATA_TYPE: "ST", NAME: "Alternate Identifier" },
    { DATA_TYPE: "FT", NAME: "Alternate Formatted Text" },
    { DATA_TYPE: "ID", NAME: "Name of Alternate Coding System" },
  ],
  CNE: [
    { DATA_TYPE: "ST", NAME: "Identifier" },
    { DATA_TYPE: "ST", NAME: "Text" },
    { DATA_TYPE: "ID", NAME: "Name of Coding System" },
    { DATA_TYPE: "ST", NAME: "Alternate Identifier" },
    { DATA_TYPE: "ST", NAME: "Alternate Text" },
    { DATA_TYPE: "ID", NAME: "Name of Alternate Coding System" },
    { DATA_TYPE: "ST", NAME: "Coding System Version ID" },
    { DATA_TYPE: "ST", NAME: "Alternate Coding System Version ID" },
    { DATA_TYPE: "ST", NAME: "Original Text" },
  ],
  CNN: [
    { DATA_TYPE: "ST", NAME: "ID Number" },
    { DATA_TYPE: "ST", NAME: "Family Name" },
    { DATA_TYPE: "ST", NAME: "Given Name" },
    { DATA_TYPE: "ST", NAME: "Second and Further Given Names or Initials Thereof" },
    { DATA_TYPE: "ST", NAME: "Suffix (e.g., JR or III)" },
    { DATA_TYPE: "ST", NAME: "Prefix (e.g., DR)" },
    { DATA_TYPE: "IS", NAME: "Degree (e.g., MD" },
    { DATA_TYPE: "IS", NAME: "Source Table" },
    { DATA_TYPE: "IS", NAME: "Assigning Authority   - Namespace ID" },
    { DATA_TYPE: "ST", NAME: "Assigning Authority  - Universal ID" },
    { DATA_TYPE: "ID", NAME: "Assigning Authority  - Universal ID Type" },
  ],
  CP: [
    {
      DATA_TYPE: "MO", NAME: "Price", TYPE: "FIELD_GROUP", children: [
        { DATA_TYPE: "NM", NAME: "Quantity" },
        { DATA_TYPE: "ID", NAME: "Denomination" },
      ]
    },
    { DATA_TYPE: "ID", NAME: "Price Type" },
    { DATA_TYPE: "NM", NAME: "From Value" },
    { DATA_TYPE: "NM", NAME: "To Value" },
    {
      DATA_TYPE: "CE", NAME: "Range Units", TYPE: "FIELD_GROUP", children: [
        { DATA_TYPE: "ST", NAME: "Identifier" },
        { DATA_TYPE: "ST", NAME: "Text" },
        { DATA_TYPE: "ID", NAME: "Name of Coding System" },
        { DATA_TYPE: "ST", NAME: "Alternate Identifier" },
        { DATA_TYPE: "ST", NAME: "Alternate Text" },
        { DATA_TYPE: "ID", NAME: "Name of Alternate Coding System" },
      ]
    },
    { NAME: "Range Type", DATA_TYPE: "ID" },
  ],
  CQ: [
    { DATA_TYPE: "NM", NAME: "Quantity" },
    {
      DATA_TYPE: "CE", NAME: "Units", TYPE: "FIELD_GROUP", children: [
        { DATA_TYPE: "ST", NAME: "Identifier" },
        { DATA_TYPE: "ST", NAME: "Text" },
        { DATA_TYPE: "ID", NAME: "Name of Coding System" },
        { DATA_TYPE: "ST", NAME: "Alternate Identifier" },
        { DATA_TYPE: "ST", NAME: "Alternate Text" },
        { DATA_TYPE: "ID", NAME: "Name of Alternate Coding System" },
      ]
    },
  ],
  CSU: [
    { DATA_TYPE: "NM", NAME: "Channel Sensitivity" },
    { DATA_TYPE: "ST", NAME: "Unit of Measure Identifier" },
    { DATA_TYPE: "ST", NAME: "Unit of Measure Description" },
    { DATA_TYPE: "ID", NAME: "Unit of Measure Coding System" },
    { DATA_TYPE: "ST", NAME: "Alternate Unit of Measure Identifier" },
    { DATA_TYPE: "ST", NAME: "Alternate Unit of Measure Description" },
    { DATA_TYPE: "ID", NAME: "Alternate Unit of Measure Coding System" },
  ],
  CWE: [
    { DATA_TYPE: "ST", NAME: "Identifier" },
    { DATA_TYPE: "ST", NAME: "Text" },
    { DATA_TYPE: "ID", NAME: "Name of Coding System" },
    { DATA_TYPE: "ST", NAME: "Alternate Identifier" },
    { DATA_TYPE: "ST", NAME: "Alternate Text" },
    { DATA_TYPE: "ID", NAME: "Name of Alternate Coding System" },
    { DATA_TYPE: "ST", NAME: "Coding System Version ID" },
    { DATA_TYPE: "ST", NAME: "Alternate Coding System Version ID" },
    { DATA_TYPE: "ST", NAME: "Original Text" },
  ],
  CX: [
    {
      "DATA_TYPE": "ST",
      "NAME": "ID Number",
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Check Digit"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Check Digit Scheme"
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Assigning Authority",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Identifier Type Code"
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Assigning Facility",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "DT",
      "NAME": "Effective Date"
    },
    {
      "DATA_TYPE": "DT",
      "NAME": "Expiration Date"
    },
    {
      "DATA_TYPE": "CWE",
      "NAME": "Assigning Jurisdiction",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    },
    {
      "DATA_TYPE": "CWE",
      "NAME": "Assigning Agency or Department",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ],
    }
  ],
  DDI: [
    {
      "DATA_TYPE": "NM",
      "NAME": "Delay Days"
    },
    {
      "DATA_TYPE": "MO",
      "NAME": "Monetary Amount",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "NM",
          "NAME": "Quantity"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Denomination"
        }
      ]
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Number of Days"
    }
  ],
  DIN: [
    {
      "DATA_TYPE": "TS",
      "NAME": "Date",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    },
    {
      "DATA_TYPE": "CE",
      "NAME": "Institution Name",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        }
      ]
    }
  ],
  DLD: [
    {
      "DATA_TYPE": "IS",
      "NAME": "Discharge Location",
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Effective Date",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    }
  ],
  DLN: [
    {
      "DATA_TYPE": "ST",
      "NAME": "License Number",
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Issuing State, Province, Country"
    },
    {
      "DATA_TYPE": "DT",
      "NAME": "Expiration Date"
    }
  ],
  DLT: [
    {
      "DATA_TYPE": "NR",
      "NAME": "Normal Range",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "NM",
          "NAME": "Low Value"
        },
        {
          "DATA_TYPE": "NM",
          "NAME": "High Value"
        }
      ]
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Numeric Threshold"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Change Computation"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Days Retained"
    }
  ],
  DR: [
    {
      "DATA_TYPE": "TS",
      "NAME": "Range Start Date_Time",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Range End Date_Time",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    }
  ],
  DT: "field",
  DTM: "field",
  DTN: [
    {
      "DATA_TYPE": "IS",
      "NAME": "Day Type"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Number of Days"
    }
  ],
  ED: [
    {
      "DATA_TYPE": "HD",
      "NAME": "Source Application",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Type of Data"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Data Subtype"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Encoding"
    },
    {
      "DATA_TYPE": "TX",
      "NAME": "Data"
    }
  ],
  EI: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Entity Identifier"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Namespace ID"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Universal ID"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Universal ID Type"
    }
  ],
  EIP: [
    {
      "DATA_TYPE": "EI",
      "NAME": "Placer Assigned Identifier",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Entity Identifier"
        },
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "EI",
      "NAME": "Filler Assigned Identifier",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Entity Identifier"
        },
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    }
  ],
  ELD: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Segment ID"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Segment Sequence"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Field Position"
    },
    {
      "DATA_TYPE": "CE",
      "NAME": "Code Identifying Error",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        }
      ]
    }
  ],
  ERL: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Segment ID"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Segment Sequence"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Field Position"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Field Repetition"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Component Number"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Sub-Component Number"
    }
  ],
  FC: [
    {
      "DATA_TYPE": "IS",
      "NAME": "Financial Class Code"
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Effective Date",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    }
  ],
  FN: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Surname"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Own Surname Prefix"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Own Surname"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Surname Prefix From Partner_Spouse"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Surname From Partner_Spouse"
    }
  ],
  FT: "field",
  GTS: "field",
  HD: [
    {
      "DATA_TYPE": "IS",
      "NAME": "Namespace ID"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Universal ID"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Universal ID Type"
    }
  ],
  ICD: [
    {
      "DATA_TYPE": "IS",
      "NAME": "Certification Patient Type"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Certification Required"
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Date_Time Certification Required",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    }
  ]
  ,
  ID: "field",
  IS: "field",
  JCC: [
    {
      "DATA_TYPE": "IS",
      "NAME": "Job Code"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Job Class"
    },
    {
      "DATA_TYPE": "TX",
      "NAME": "Job Description Text"
    }
  ],
  LA1: [
    {
      "DATA_TYPE": "IS",
      "NAME": "Point of Care"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Room"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Bed"
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Facility",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Location Status"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Patient Location Type"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Building"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Floor"
    },
    {
      "DATA_TYPE": "AD",
      "NAME": "Address"
    }
  ],
  LA2: [
    {
      "DATA_TYPE": "IS",
      "NAME": "Point of Care"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Room"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Bed"
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Facility",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Location Status"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Patient Location Type"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Building"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Floor"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Street Address"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Other Designation"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "City"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "State or Province"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Zip or Postal Code"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Country"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Address Type"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Other Geographic Designation"
    }
  ],
  MA: [
    {
      "DATA_TYPE": "NM",
      "NAME": "Sample 1 From Channel 1"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Sample 1 From Channel 2"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Sample 1 From Channel N"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Sample 2 From Channel 1"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Sample 2 From Channel N"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Sample N From Channel N"
    }
  ],
  MO: [
    {
      "DATA_TYPE": "NM",
      "NAME": "Quantity"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Denomination"
    }
  ],
  MOC: [
    {
      "DATA_TYPE": "MO",
      "NAME": "Monetary Amount",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "NM",
          "NAME": "Quantity"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Denomination"
        }
      ]
    },
    {
      "DATA_TYPE": "CE",
      "NAME": "Charge Code",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        }
      ]
    }
  ],
  MOP: [
    {
      "DATA_TYPE": "ID",
      "NAME": "Money or Percentage Indicator"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Money or Percentage Quantity"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Currency Denomination"
    }
  ],
  MSG: [
    {
      "DATA_TYPE": "ID",
      "NAME": "Message Code"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Trigger Event"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Message Structure"
    }
  ],
  NA: [
    {
      "DATA_TYPE": "NM",
      "NAME": "Value1"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Value2"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Value3"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Value4"
    }
  ],
  NDL: [
    {
      "DATA_TYPE": "CNN",
      "NAME": "Name",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "ID Number"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Family Name"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Given Name"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Second and Further Given Names or Initials Thereof"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Suffix (e.g., JR or III)"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Prefix (e.g., DR)"
        },
        {
          "DATA_TYPE": "IS",
          "NAME": "Degree (e.g., MD"
        },
        {
          "DATA_TYPE": "IS",
          "NAME": "Source Table"
        },
        {
          "DATA_TYPE": "IS",
          "NAME": "Assigning Authority - Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Assigning Authority - Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Assigning Authority - Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Start Date_time",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "End Date_time",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Point of Care"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Room"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Bed"
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Facility",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Location Status"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Patient Location Type"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Building"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Floor"
    }
  ],
  NM: "field",
  NR: [
    {
      "DATA_TYPE": "NM",
      "NAME": "Low Value"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "High Value"
    }
  ],
  OCD: [
    {
      "DATA_TYPE": "CNE",
      "NAME": "Occurrence Code",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    },
    {
      "DATA_TYPE": "DT",
      "NAME": "Occurrence Date"
    }
  ],
  OSD: [
    {
      "DATA_TYPE": "ID",
      "NAME": "Sequence_Results Flag"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Placer Order Number: Entity Identifier"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Placer Order Number: Namespace ID"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Filler Order Number: Entity Identifier"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Filler Order Number: Namespace ID"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Sequence Condition Value"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Maximum Number of Repeats"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Placer Order Number: Universal ID"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Placer Order Number: Universal ID Type"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Filler Order Number: Universal ID"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Filler Order Number: Universal ID Type"
    }
  ],
  OSP: [
    {
      "DATA_TYPE": "CNE",
      "NAME": "Occurence Span Code",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        },
        
      ]
    },
    {
      "DATA_TYPE": "DT",
      "NAME": "Occurrence Span Start Date"
    },
    {
      "DATA_TYPE": "DT",
      "NAME": "Occurrence Span Stop Date"
    }
  ],
  PIP: [
    {
      "DATA_TYPE": "CE",
      "NAME": "Privilege",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        }
      ]
    },
    {
      "DATA_TYPE": "CE",
      "NAME": "Privilege Class",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        }
      ]
    },
    {
      "DATA_TYPE": "DT",
      "NAME": "Expiration Date"
    },
    {
      "DATA_TYPE": "DT",
      "NAME": "Activation Date"
    },
    {
      "DATA_TYPE": "EI",
      "NAME": "Facility",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Entity Identifier"
        },
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    }
  ],
  PL: [
    {
      "DATA_TYPE": "IS",
      "NAME": "Point of Care"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Room"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Bed"
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Facility",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Location Status"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Person Location Type"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Building"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Floor"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Location Description"
    },
    {
      "DATA_TYPE": "EI",
      "NAME": "Comprehensive Location Identifier",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Entity Identifier"
        },
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Assigning Authority for Location",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    }
  ],
  PLN: [
    {
      "DATA_TYPE": "ST",
      "NAME": "ID Number"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Type of ID Number"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "State_other Qualifying Information"
    },
    {
      "DATA_TYPE": "DT",
      "NAME": "Expiration Date"
    }
  ],
  PPN: [
    {
      "DATA_TYPE": "ST",
      "NAME": "ID Number"
    },
    {
      "DATA_TYPE": "FN",
      "NAME": "Family Name",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Surname"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Own Surname Prefix"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Own Surname"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Surname Prefix From Partner_Spouse"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Surname From Partner_Spouse"
        }
      ]
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Given Name"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Second and Further Given Names or Initials Thereof"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Suffix (e.g., JR or III)"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Prefix (e.g., DR)"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Degree (e.g., MD)"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Source Table"
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Assigning Authority",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Name Type Code"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Identifier Check Digit"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Check Digit Scheme"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Identifier Type Code"
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Assigning Facility",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Date_Time Action Performed",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Name Representation Code"
    },
    {
      "DATA_TYPE": "CE",
      "NAME": "Name Context",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        }
      ]
    },
    {
      "DATA_TYPE": "DR",
      "NAME": "Name Validity Range",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "TS",
          "NAME": "Range Start Date_Time"
        },
        {
          "DATA_TYPE": "TS",
          "NAME": "Range End Date_Time"
        }
      ]
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Name Assembly Order"
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Effective Date",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Expiration Date",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Professional Suffix"
    },
    {
      "DATA_TYPE": "CWE",
      "NAME": "Assigning Jurisdiction",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    },
    {
      "DATA_TYPE": "CWE",
      "NAME": "Assigning Agency or Department",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    }
  ],
  PRL: [
    {
      "DATA_TYPE": "CE",
      "NAME": "Parent Observation Identifier",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        }
      ]
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Parent Observation Sub-identifier"
    },
    {
      "DATA_TYPE": "TX",
      "NAME": "Parent Observation Value Descriptor"
    }
  ],
  PT: [
    {
      "DATA_TYPE": "ID",
      "NAME": "Processing ID"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Processing Mode"
    }
  ],
  PTA: [
    {
      "DATA_TYPE": "IS",
      "NAME": "Policy Type"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Amount Class"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Money or Percentage Quantity"
    },
    {
      "NAME": "Money or Percentage",
      "DATA_TYPE": "MOP",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ID",
          "NAME": "Money or Percentage Indicator"
        },
        {
          "DATA_TYPE": "NM",
          "NAME": "Money or Percentage Quantity"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Currency Denomination"
        }
      ]
    }
  ],
  QIP: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Segment Field Name"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Values"
    }
  ],
  QSC: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Segment Field Name"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Relational Operator"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Value"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Relational Conjunction"
    }
  ],
  RCD: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Segment Field Name"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "HL7 Data Type"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Maximum Column Width"
    }
  ],
  RFR: [
    {
      "DATA_TYPE": "NR",
      "NAME": "Numeric Range",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "NM",
          "NAME": "Low Value"
        },
        {
          "DATA_TYPE": "NM",
          "NAME": "High Value"
        }
      ]
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Administrative Sex"
    },
    {
      "DATA_TYPE": "NR",
      "NAME": "Age Range",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "NM",
          "NAME": "Low Value"
        },
        {
          "DATA_TYPE": "NM",
          "NAME": "High Value"
        }
      ]
    },
    {
      "DATA_TYPE": "NR",
      "NAME": "Gestational Age Range",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "NM",
          "NAME": "Low Value"
        },
        {
          "DATA_TYPE": "NM",
          "NAME": "High Value"
        }
      ]
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Species"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Race_subspecies"
    },
    {
      "DATA_TYPE": "TX",
      "NAME": "Conditions"
    }
  ],
  RI: [
    {
      "DATA_TYPE": "IS",
      "NAME": "Repeat Pattern"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Explicit Time Interval"
    }
  ],
  RMC: [
    {
      "DATA_TYPE": "IS",
      "NAME": "Room Type",
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Amount Type"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Coverage Amount"
    },
    {
      "DATA_TYPE": "MOP",
      "NAME": "Money or Percentage",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ID",
          "NAME": "Money or Percentage Indicator",
        },
        {
          "DATA_TYPE": "NM",
          "NAME": "Money or Percentage Quantity",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Currency Denomination"
        }
      ]
    }
  ],
  RP: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Pointer"
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Application ID",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Type of Data"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Subtype"
    }
  ],
  RPT: [
    {
      "DATA_TYPE": "CWE",
      "NAME": "Repeat Pattern Code",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Calendar Alignment"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Phase Range Begin Value"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Phase Range End Value"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Period Quantity"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Period Units"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Institution Specified Time"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Event"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Event Offset Quantity"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Event Offset Units"
    },
    {
      "DATA_TYPE": "GTS",
      "NAME": "General Timing Specification"
    }
  ],
  SAD: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Street or Mailing Address"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Street Name"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Dwelling Number"
    }
  ],
  SCV: [
    {
      "DATA_TYPE": "CWE",
      "NAME": "Parameter Class",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Parameter Value"
    }
  ],
  SI: "field",
  SN: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Comparator"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Num1"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Separator_Suffix"
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Num2"
    }
  ],
  SPD: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Specialty Name",
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Governing Board"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Eligible or Certified"
    },
    {
      "DATA_TYPE": "DT",
      "NAME": "Date of Certification"
    }
  ],
  SPS: [
    {
      "DATA_TYPE": "CWE",
      "NAME": "Specimen Source Name or Code",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    },
    {
      "DATA_TYPE": "CWE",
      "NAME": "Additives",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    },
    {
      "DATA_TYPE": "TX",
      "NAME": "Specimen Collection Method"
    },
    {
      "DATA_TYPE": "CWE",
      "NAME": "Body Site",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    },
    {
      "DATA_TYPE": "CWE",
      "NAME": "Site Modifier",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    },
    {
      "DATA_TYPE": "CWE",
      "NAME": "Collection Method Modifier Code",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    },
    {
      "DATA_TYPE": "CWE",
      "NAME": "Specimen Role",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    }
  ],
  SRT: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Sort-by Field",
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Sequencing"
    }
  ],
  ST: "field",
  TM: "field",
  TQ: [
    {
      "DATA_TYPE": "CQ",
      "NAME": "Quantity",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "NM",
          "NAME": "Quantity"
        },
        {
          "DATA_TYPE": "CE",
          "NAME": "Units"
        }
      ]
    },
    {
      "DATA_TYPE": "RI",
      "NAME": "Interval",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Repeat Pattern"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Explicit Time Interval"
        }
      ]
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Duration"
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Start Date_Time",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "End Date_Time",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Priority"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Condition"
    },
    {
      "DATA_TYPE": "TX",
      "NAME": "Text"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Conjunction"
    },
    {
      "DATA_TYPE": "OSD",
      "NAME": "Order Sequencing",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ID",
          "NAME": "Sequence_Results Flag",
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Placer Order Number: Entity Identifier",
        },
        {
          "DATA_TYPE": "IS",
          "NAME": "Placer Order Number: Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Filler Order Number: Entity Identifier",
        },
        {
          "DATA_TYPE": "IS",
          "NAME": "Filler Order Number: Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Sequence Condition Value"
        },
        {
          "DATA_TYPE": "NM",
          "NAME": "Maximum Number of Repeats"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Placer Order Number: Universal ID",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Placer Order Number: Universal ID Type"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Filler Order Number: Universal ID",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Filler Order Number: Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "CE",
      "NAME": "Occurrence Duration",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        }
      ]
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Total Occurrences"
    }
  ],
  TS: [
    {
      "DATA_TYPE": "DTM",
      "NAME": "Time",
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Degree of Precision"
    }
  ],
  TX: "field",
  UVC: [
    {
      "DATA_TYPE": "CNE",
      "NAME": "Value Code",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier",
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    },
    {
      "DATA_TYPE": "MO",
      "NAME": "Value Amount",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "NM",
          "NAME": "Quantity"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Denomination"
        }
      ]
    }
  ],
  VH: [
    {
      "DATA_TYPE": "ID",
      "NAME": "Start Day Range"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "End Day Range"
    },
    {
      "DATA_TYPE": "TM",
      "NAME": "Start Hour Range"
    },
    {
      "DATA_TYPE": "TM",
      "NAME": "End Hour Range"
    }
  ],
  VID: [
    {
      "DATA_TYPE": "ID",
      "NAME": "Version ID"
    },
    {
      "DATA_TYPE": "CE",
      "NAME": "Internationalization Code",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        }
      ]
    },
    {
      "DATA_TYPE": "CE",
      "NAME": "International Version ID",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        }
      ]
    }
  ],
  VR: [
    {
      "DATA_TYPE": "ST",
      "NAME": "First Data Code Value"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Last Data Code Value"
    }
  ],
  WVI: [
    {
      "DATA_TYPE": "NM",
      "NAME": "Channel Number",
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Channel Name"
    }
  ],
  WVS: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Source One Name",
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Source Two Name"
    }
  ],
  XAD: [
    {
      "DATA_TYPE": "SAD",
      "NAME": "Street Address",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Street or Mailing Address"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Street Name"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Dwelling Number"
        }
      ]
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Other Designation"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "City"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "State or Province"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Zip or Postal Code"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Country"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Address Type"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Other Geographic Designation"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "County_Parish Code"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Census Tract"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Address Representation Code"
    },
    {
      "DATA_TYPE": "DR",
      "NAME": "Address Validity Range",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "TS",
          "NAME": "Range Start Date_Time"
        },
        {
          "DATA_TYPE": "TS",
          "NAME": "Range End Date_Time"
        }
      ]
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Effective Date",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Expiration Date",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    }
  ],
  XCN: [
    {
      "DATA_TYPE": "ST",
      "NAME": "ID Number"
    },
    {
      "DATA_TYPE": "FN",
      "NAME": "Family Name",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Surname",
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Own Surname Prefix"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Own Surname"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Surname Prefix From Partner_Spouse"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Surname From Partner_Spouse"
        }
      ]
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Given Name"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Second and Further Given Names or Initials Thereof"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Suffix (e.g., JR or III)"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Prefix (e.g., DR)"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Degree (e.g., MD)"
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Source Table"
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Assigning Authority",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Name Type Code"
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Identifier Check Digit"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Check Digit Scheme"
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Identifier Type Code"
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Assigning Facility",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type"
        }
      ]
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Name Representation Code"
    },
    {
      "DATA_TYPE": "CE",
      "NAME": "Name Context",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        }
      ]
    },
    {
      "DATA_TYPE": "DR",
      "NAME": "Name Validity Range",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "TS",
          "NAME": "Range Start Date_Time"
        },
        {
          "DATA_TYPE": "TS",
          "NAME": "Range End Date_Time"
        }
      ]
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Name Assembly Order"
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Effective Date",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Expiration Date",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Professional Suffix"
    },
    {
      "DATA_TYPE": "CWE",
      "NAME": "Assigning Jurisdiction",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    },
    {
      "DATA_TYPE": "CWE",
      "NAME": "Assigning Agency or Department",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Coding System Version ID"
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Original Text"
        }
      ]
    }
  ],
  XON: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Organization Name",
    },
    {
      "DATA_TYPE": "IS",
      "NAME": "Organization Name Type Code",
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "ID Number",
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Check Digit",
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Check Digit Scheme",
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Assigning Authority",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID",
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type",
        }
      ]
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Identifier Type Code",
    },
    {
      "DATA_TYPE": "HD",
      "NAME": "Assigning Facility",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "IS",
          "NAME": "Namespace ID",
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Universal ID",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Universal ID Type",
        }
      ]
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Name Representation Code",
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Organization Identifier",
    }
  ],
  XPN: [
    {
      "DATA_TYPE": "FN",
      "NAME": "Family Name",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Surname",
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Own Surname Prefix",
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Own Surname",
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Surname Prefix From Partner_Spouse",
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Surname From Partner_Spouse",
        },
      ]
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Given Name",
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Second and Further Given Names or Initials Thereof",
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Suffix (e.g., JR or III)",
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Prefix (e.g., DR)",
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Degree (e.g., MD)",
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Name Type Code",
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Name Representation Code",
    },
    {
      "DATA_TYPE": "CE",
      "NAME": "Name Context",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "ST",
          "NAME": "Identifier",
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Text",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Coding System",
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Identifier",
        },
        {
          "DATA_TYPE": "ST",
          "NAME": "Alternate Text",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Name of Alternate Coding System",
        }
      ]
    },
    {
      "DATA_TYPE": "DR",
      "NAME": "Name Validity Range",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "TS",
          "NAME": "Range Start Date_Time",
        },
        {
          "DATA_TYPE": "TS",
          "NAME": "Range End Date_Time",
        }
      ]
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Name Assembly Order",
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Effective Date",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time",
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision",
        }
      ]
    },
    {
      "DATA_TYPE": "TS",
      "NAME": "Expiration Date",
      "TYPE": "FIELD_GROUP",
      "children": [
        {
          "DATA_TYPE": "DTM",
          "NAME": "Time"
        },
        {
          "DATA_TYPE": "ID",
          "NAME": "Degree of Precision"
        }
      ]
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Professional Suffix"
    }
  ],
  XTN: [
    {
      "DATA_TYPE": "ST",
      "NAME": "Telephone Number",
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Telecommunication Use Code",
    },
    {
      "DATA_TYPE": "ID",
      "NAME": "Telecommunication Equipment Type",
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Email Address",
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Country Code",
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Area_City Code",
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Local Number",
    },
    {
      "DATA_TYPE": "NM",
      "NAME": "Extension",
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Any Text",
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Extension Prefix",
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Speed Dial Code",
    },
    {
      "DATA_TYPE": "ST",
      "NAME": "Unformatted Telephone number",
    }
  ],
  var: "var",
}

const dropdownOptions = Object.keys(datatypes)


